// Auth session logic: what a sign-in response turns into, when the access
// token gets refreshed, and which 401s end the session. Pure on purpose (no
// storage, no fetch, no React): `authStore.js` wires it to localStorage and the
// API. The same rules, and the same storage layout, as mobile-next's
// `lib/auth/authSession.ts`, whose web export runs on this origin.
//
// Contract: docs/features/auth-sessions-and-ticket-claims.md ("Auth sessions").

/** Sent on sign-in requests to get an access and refresh token pair back. */
export const AUTH_OPT_IN_HEADERS = Object.freeze({ "X-Congrs-Auth": "2" });

/** Refresh ahead of a request when the access token expires sooner than this. */
export const REFRESH_LEEWAY_MS = 60 * 1000;

/** 401 codes that end the session outright: refreshing cannot help. */
const SESSION_ENDED_CODES = new Set(["SESSION_REVOKED", "ACCOUNT_UNAVAILABLE"]);

/**
 * @typedef {Object} StoredSession
 * @property {string} accessToken
 * @property {string | null} refreshToken - `null` for a legacy single token:
 *   no refresh, any 401 signs out.
 * @property {number | null} accessTokenExpiresAt - Epoch ms, this browser's clock.
 * @property {number | null} refreshTokenExpiresAt - Epoch ms as the server sent it.
 */

/** An ISO date or an epoch in seconds or milliseconds, as epoch ms. */
export function toEpochMs(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }
  if (typeof value === "string" && value.trim()) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return toEpochMs(numeric);
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}

/** The `iat` and `exp` claims of a JWT, if it decodes. Not verified. */
function jwtTimes(token) {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const { iat, exp } = JSON.parse(atob(padded));
    return typeof iat === "number" && typeof exp === "number"
      ? { iat, exp }
      : null;
  } catch {
    return null;
  }
}

/**
 * When the access token expires, on this browser's clock: the token's own
 * lifetime (`exp - iat`) from now, so a skewed clock doesn't refresh on every
 * request or never. The server's absolute time is the fallback.
 */
function localAccessExpiry(accessToken, serverExpiresAt, now) {
  const times = jwtTimes(accessToken);
  if (times && times.exp > times.iat) {
    return now + (times.exp - times.iat) * 1000;
  }
  return toEpochMs(serverExpiresAt);
}

/**
 * What to store for a sign-in (or refresh) response. Without a `refreshToken`
 * it is a legacy single token. Throws when the response carries no token.
 *
 * @returns {StoredSession}
 */
export function sessionFromResponse(response, now) {
  const accessToken = response?.accessToken ?? response?.token;
  if (typeof accessToken !== "string" || !accessToken) {
    throw new Error("Sign-in response has no token");
  }

  if (typeof response.refreshToken !== "string" || !response.refreshToken) {
    return {
      accessToken,
      refreshToken: null,
      accessTokenExpiresAt: null,
      refreshTokenExpiresAt: null,
    };
  }

  return {
    accessToken,
    refreshToken: response.refreshToken,
    accessTokenExpiresAt: localAccessExpiry(
      accessToken,
      response.accessTokenExpiresAt,
      now
    ),
    refreshTokenExpiresAt: toEpochMs(response.refreshTokenExpiresAt),
  };
}

/**
 * `/auth/refresh` refused the refresh token, as opposed to failing to answer.
 * Network errors (no `status`), timeouts, rate limits and 5xx keep the session.
 */
function isRefreshRejected(error) {
  const status = error?.status;
  if (typeof status !== "number") return false;
  return status >= 400 && status < 500 && status !== 408 && status !== 429;
}

/**
 * @param {Object} deps
 * @param {{ read: () => Promise<StoredSession | null>, write: (s: StoredSession) => Promise<void>, clear: () => Promise<void> }} deps.storage
 * @param {(refreshToken: string) => Promise<Object>} deps.requestRefresh -
 *   `POST /auth/refresh`. Rejects with an error carrying `status` when the API
 *   answered, any other error when it could not be reached.
 * @param {() => void} deps.onSessionEnded - The session is over and its
 *   tokens are already cleared: sign out.
 * @param {() => number} [deps.now]
 * @param {(fn: () => Promise<any>) => Promise<any>} [deps.withLock] - Runs the
 *   refresh under a lock shared with other tabs.
 */
export function createSessionManager({
  storage,
  requestRefresh,
  onSessionEnded,
  now = Date.now,
  withLock = (fn) => fn(),
}) {
  let inFlight = null;
  // Bumped whenever the stored session is replaced or cleared from outside
  // (sign-in, sign-out), so a refresh that lands afterwards can't write the
  // old session back.
  let generation = 0;
  const invalidate = () => {
    generation += 1;
  };

  const isExpiring = (session) =>
    session.accessTokenExpiresAt !== null &&
    session.accessTokenExpiresAt - now() < REFRESH_LEEWAY_MS;

  const endSession = async () => {
    invalidate();
    await storage.clear();
    onSessionEnded();
  };

  const currentToken = async () => (await storage.read())?.accessToken ?? null;

  const runRefresh = (staleToken) =>
    withLock(async () => {
      const stored = await storage.read();
      if (!stored) return null;
      // Someone else (another request, another tab) refreshed while this one
      // waited: use their token instead of spending the refresh token again.
      if (stored.accessToken !== staleToken && !isExpiring(stored)) {
        return stored.accessToken;
      }
      if (!stored.refreshToken) return null;

      const startedAt = generation;
      let body;
      try {
        body = await requestRefresh(stored.refreshToken);
      } catch (error) {
        // Signed out or in again meanwhile: this refresh no longer matters.
        if (generation !== startedAt) return currentToken();
        if (!isRefreshRejected(error)) throw error;
        await endSession();
        return null;
      }

      if (generation !== startedAt) return currentToken();
      const next = sessionFromResponse(body, now());
      await storage.write(next);
      return next.accessToken;
    });

  /**
   * Swap the access token for a new pair; concurrent callers share one call.
   * Resolves with the new access token, or `null` when there is no session to
   * refresh. Rejects, keeping the tokens, when the API could not be reached.
   */
  const refresh = (staleToken) => {
    if (!inFlight) {
      inFlight = runRefresh(staleToken).finally(() => {
        inFlight = null;
      });
    }
    return inFlight;
  };

  /**
   * The access token to send, refreshed first when it expires within
   * `REFRESH_LEEWAY_MS`. Never rejects: when the refresh can't reach the API
   * the current token goes out and its request surfaces the failure.
   */
  const getAccessToken = async () => {
    const stored = await storage.read();
    if (!stored) return null;
    if (!stored.refreshToken || !isExpiring(stored)) return stored.accessToken;

    try {
      return await refresh(stored.accessToken);
    } catch {
      return stored.accessToken;
    }
  };

  /**
   * What a 401 on a request sent with `token` means. Resolves with a token to
   * retry the request with once, or `null` to fail it (having ended the
   * session when the rules say so). Rejects, keeping the tokens, when the
   * refresh it needed could not reach the API.
   *
   * - Legacy token (no refresh token): sign out, as before sessions.
   * - `SESSION_REVOKED`, `ACCOUNT_UNAVAILABLE`: sign out.
   * - Anything else (`TOKEN_EXPIRED`, `UNAUTHORIZED`, no code): refresh, retry.
   * - A 401 on the retry: sign out.
   */
  const handleUnauthorized = async ({ token, code, isRetry }) => {
    const stored = await storage.read();
    // Already signed out (a request that outlived its session).
    if (!stored) return null;

    // The token changed while the request was out: a refresh elsewhere or a
    // new sign-in. Try the current one, but never loop.
    if (stored.accessToken !== token) {
      return isRetry ? null : stored.accessToken;
    }

    if (!stored.refreshToken || isRetry || SESSION_ENDED_CODES.has(code)) {
      await endSession();
      return null;
    }

    return refresh(token);
  };

  return { getAccessToken, refresh, handleUnauthorized, invalidate };
}
